import { memo, useEffect, useState } from 'react';
import { roundBook, fmtBook } from '../../utils/casinoBook';

const CDN = 'https://wver.sprintstaticdata.com/v230/static/front/img/cards';

const SUITS = [
  { nat: 'K of spade', king: `${CDN}/KSS.png` },
  { nat: 'K of heart', king: `${CDN}/KHH.png` },
  { nat: 'K of club', king: `${CDN}/KCC.png` },
  { nat: 'K of diamond', king: `${CDN}/KDD.png` },
];

// col-4 groups: [lo, hi] with their collapse ids
const WIN_COLS = [
  [{ label: 'Win with 5', rid: 'fancy2' }, { label: 'Win with 15', rid: 'fancy5' }],
  [{ label: 'Win with 6', rid: 'fancy3' }, { label: 'Win with 16', rid: 'fancy6' }],
  [{ label: 'Win with 7', rid: 'fancy4' }, { label: 'Win with 17', rid: 'fancy7' }],
];

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}
function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}
function fmtAmt(n) {
  const v = Number(n) || 0;
  if (v >= 100000) return `${v / 100000}L`;
  if (v >= 1000) return `${v / 1000}K`;
  return String(v);
}
function bookOf(exposures, name) {
  return roundBook(exposures?.[name] ?? 0);
}
function bookCls(v) {
  return v < 0 ? 'book-red' : v > 0 ? 'book-green' : 'book-black';
}

// wrapped range-icon (desktop K-suit/total/win-with + mobile win-with)
function RangeIcon({ id, min, max, wrapClass = 'range-icon d-inline-block ml-2' }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={wrapClass}>
      <i data-toggle="collapse" data-target={`#${id}`} className="fas fa-info-circle float-right" onClick={() => setOpen((v) => !v)}></i>
      {' '}
      <div id={id} className={`collapse icon-range${open ? ' show' : ''}`}>
        {'\n                            R:'}
        <span>{fmtAmt(min)}</span>
        -
        <span>{fmtAmt(max)}</span>
      </div>
    </div>
  );
}

// bare info-circle + collapse (mobile K-suit / total rows)
function MobileRange({ id, min, max }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <i
        data-toggle="collapse"
        data-target={`#${id}`}
        aria-expanded={open ? 'true' : 'false'}
        className={`fas fa-info-circle float-right${open ? '' : ' collapsed'}`}
        onClick={() => setOpen((v) => !v)}
      ></i>
      {' '}
      <div id={id} className={`icon-range collapse${open ? ' show' : ''}`}>
        {'\n                          R:'}
        <span>{fmtAmt(min)}</span>
        -
        <span>{fmtAmt(max)}</span>
      </div>
    </>
  );
}

// Race20 bet table: K-of-suit boxes (win the suit), Total points/cards Yes/No,
// Win-with 5/6/7/15/16/17 fancy. book-black hamesha + collapse ranges.
// Desktop rows directly in casino-detail; mobile in d-none-big — only one in the DOM (reference v-if).
// Total points / Total cards: the reference prints FIXED rates under the odds —
// Total points No 100 / Yes 100, Total cards No 105 / Yes 90 — and 0 while the
// market is not OPEN. It never shows the feed's ls/bs (100000).
const TOTAL_RATES = [{ lay: 100, back: 100 }, { lay: 105, back: 90 }];
const totalRate = (item, i, side) => (item?.gstatus === 'OPEN' ? TOTAL_RATES[i][side] : 0);

const BetTableRace20 = memo(function BetTableRace20({ gameData, onBet, exposures, totalCards, totalPoints }) {
  const sub = gameData?.sub || [];
  const handleBet = onBet || (() => {});
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  // Reference IS_MOBILE (< 1280): between 768 and 1279 the totals sit above the
  // table as one text line (the video title box is hidden there, Race20.jsx).
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1280);

  useEffect(() => {
    const onResize = () => { setIsMobile(window.innerWidth < 768); setIsNarrow(window.innerWidth < 1280); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const findByNat = (nat) => sub.find((s) => s.nat === nat);
  const suits = SUITS.map(({ nat, king }) => ({ nat, king, item: findByNat(nat) }));
  const totalPointsItem = findByNat('Total points');
  const totalCardsItem = findByNat('Total cards');

  // 4 K-img slots, only idx shows (matches Vue v-for + v-if placeholders)
  const kImg = (idx) => SUITS.map((s, j) => (j === idx ? <img key={j} src={s.king} /> : null));

  return (
    <>
      {/* ── Desktop ── (reference: one layout in the DOM at a time, desktop rows sit directly in casino-detail) */}
      {!isMobile && (<>
        {isNarrow && (
          <div className="text-right">
            Total Cards: {totalCards} | Total Points: {totalPoints}
          </div>
        )}
        {' '}
        <div className="row row5">
          {suits.map(({ nat, item }, i) => {
            const book = bookOf(exposures, nat);
            return (
              <div key={i} className="col-6 col-md-3">
                <div className="casino-box-row">
                  <div className="casino-nation-name">
                    {kImg(i)}
                    {' '}
                    <RangeIcon id={`demo${i}`} min={item?.min ?? 100} max={item?.max ?? 300000} />
                  </div>
                  {' '}
                  <div className="casino-bl-box">
                    <div className={`back casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}>
                      <span className="casino-box-odd">{item?.b ?? 0}</span>
                    </div>
                    {' '}
                    <div className={`lay casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'lay', item.l, item)}>
                      <span className="casino-box-odd">{item?.l ?? 0}</span>
                    </div>
                  </div>
                  {' '}
                  <div className={`casino-nation-name ${bookCls(book)}`}>{fmtBook(book)}</div>
                </div>
              </div>
            );
          })}
        </div>
        {' '}
        <div className="row mt-2">
          <div className="col-12 col-md-4">
            {[
              { item: totalPointsItem, label: 'Total points', rid: 'tp0' },
              { item: totalCardsItem, label: 'Total cards', rid: 'tp1' },
            ].map(({ item, label, rid }, i) => {
              const book = bookOf(exposures, label);
              return (
                <div key={i}>
                  <div className="casino-yn">
                    <div></div>
                    {' '}
                    <div className="casino-bl-box">
                      <div className="casino-bl-box-item yn-header"><b>No</b></div>
                      {' '}
                      <div className="casino-bl-box-item yn-header"><b>Yes</b></div>
                    </div>
                  </div>
                  {' '}
                  <div className="casino-odds-box casino-yn">
                    <div className="casino-odds-box-bhav">
                      <b>{label}</b>
                      {' '}
                      <RangeIcon id={rid} min={item?.min ?? 100} max={item?.max ?? 100000} wrapClass="range-icon d-inline-block" />
                    </div>
                    {' '}
                    <div className="casino-bl-box">
                      <div className={`lay casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'lay', item.l, item)}>
                        <span className="casino-box-odd">{item?.l ?? 0}</span> <span>{totalRate(item, i, 'lay')}</span>
                      </div>
                      {' '}
                      <div className={`back casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}>
                        <span className="casino-box-odd">{item?.b ?? 0}</span> <span>{totalRate(item, i, 'back')}</span>
                      </div>
                    </div>
                  </div>
                  {' '}
                  <div className="casino-yn">
                    <div></div>
                    {' '}
                    <div className="casino-bl-box">
                      <div className={`casino-nation-name ${bookCls(book)}`}>{fmtBook(book)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {' '}
          <div className="col-12 col-md-8 win-with">
            <div className="row row5">
              {WIN_COLS.map((col, i) => (
                <div key={i} className="col-4">
                  {col.map(({ label, rid }, j) => {
                    const item = findByNat(label);
                    const book = bookOf(exposures, label);
                    return (
                      <div key={j} className="casino-box-row">
                        <div className="casino-nation-name">
                          <b>{label}</b>
                          {' '}
                          <RangeIcon id={rid} min={item?.min ?? 100} max={item?.max ?? 25000} />
                        </div>
                        {' '}
                        <div className="casino-bl-box">
                          <div className={`back casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}>
                            <span className="casino-box-odd">{item?.b ?? 0}</span>
                          </div>
                        </div>
                        {' '}
                        <div className={`casino-nation-name rf-minheight ${bookCls(book)}`}>{fmtBook(book)}</div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </>)}

      {/* ── Mobile ── */}
      {isMobile && (<div className="d-none-big">
        <div className="total-points">
          <div>
            <span>Total Cards:</span>
            {' '}
            <span className="text-playerb">{totalCards}</span>
          </div>
          {' '}
          <div>
            <span>Total Points:</span>
            {' '}
            <span className="text-playerb">{totalPoints}</span>
          </div>
        </div>
        {' '}
        <div>
          <div className="casino-bl-box casino-bl-box-title">
            <div className="casino-bl-box-item casino-odds-name"></div>
            {' '}
            <div className="casino-bl-box-item">Back</div>
            {' '}
            <div className="casino-bl-box-item">Lay</div>
          </div>
          {' '}
          {suits.map(({ nat, item }, i) => {
            const book = bookOf(exposures, nat);
            return (
              <div key={i} className="casino-bl-box">
                <div data-toggle="modal" className="casino-bl-box-item casino-odds-name">
                  {kImg(i)}
                  {' '}
                  <span className={bookCls(book)}>{fmtBook(book)}</span>
                  {' '}
                  <MobileRange id={`demo${i}`} min={item?.min ?? 100} max={item?.max ?? 300000} />
                </div>
                {' '}
                <div className={`back casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}>
                  <span className="casino-box-odd">{item?.b ?? 0}</span>
                </div>
                {' '}
                <div className={`lay casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'lay', item.l, item)}>
                  <span className="casino-box-odd">{item?.l ?? 0}</span>
                </div>
              </div>
            );
          })}
          {' '}
          <div className="casino-bl-box casino-bl-box-title">
            <div className="casino-bl-box-item casino-odds-name"></div>
            {' '}
            <div className="casino-bl-box-item">No</div>
            {' '}
            <div className="casino-bl-box-item">Yes</div>
          </div>
          {' '}
          {[
            { item: totalPointsItem, label: 'Total points', rid: 'noyes1' },
            { item: totalCardsItem, label: 'Total cards', rid: 'noyes2' },
          ].map(({ item, label, rid }, i) => {
            const book = bookOf(exposures, label);
            return (
              <div key={i} className="casino-bl-box">
                <div data-toggle="modal" className="casino-bl-box-item casino-odds-name">
                  <div>
                    <span className="d-block">{label}</span>
                    {' '}
                    <div className={bookCls(book)}>{fmtBook(book)}</div>
                  </div>
                  {' '}
                  <MobileRange id={rid} min={item?.min ?? 100} max={item?.max ?? 100000} />
                </div>
                {' '}
                <div className={`lay casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'lay', item.l, item)}>
                  <span className="casino-box-odd">{item?.l ?? 0}</span> <span>{totalRate(item, i, 'lay')}</span>
                </div>
                {' '}
                <div className={`back casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}>
                  <span className="casino-box-odd">{item?.b ?? 0}</span> <span>{totalRate(item, i, 'back')}</span>
                </div>
              </div>
            );
          })}
        </div>
        {' '}
        {/* reference: win-with row is a sibling of the Back/Lay + No/Yes block, not inside it */}
        <div className="row row5 win-with">
          {WIN_COLS.map((col, i) => (
            <div key={i} className="col-4">
              {col.map(({ label, rid }, j) => {
                const item = findByNat(label);
                const book = bookOf(exposures, label);
                return (
                  <div key={j} className="casino-box-row">
                    <div className="casino-nation-name">
                      <b>{label}</b>
                      {' '}
                      <RangeIcon id={rid} min={item?.min ?? 100} max={item?.max ?? 25000} />
                    </div>
                    {' '}
                    <div className="casino-bl-box">
                      <div className={`back casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}>
                        <span className="casino-box-odd">{item?.b ?? 0}</span>
                        {' '}
                        <span className={bookCls(book)}>{fmtBook(book)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>)}
    </>
  );
});

export default BetTableRace20;
